import React, { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import {
    ClassroomAnalytics,
    ModuleDashboard,
    ParamTypes,
} from "../../../interfaces/Dashboard";
import { useData } from "../../../hooks/useData";
import { useDashboardRouter } from "../../DashBoard/DashboardTeacher/routing";
import { getModuleName } from "../utils";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import Select from "../../../components/Select/Select";

import "./ClusteringNav.scss";
// Interfaces
interface Props {
    classrooms: ClassroomAnalytics[];
}

const ClusteringNav = ({ classrooms }: Props) => {
    const intl = useIntl();
    const history = useHistory();
    const { path } = useRouteMatch();
    const { classroomId, moduleId } = useParams<ParamTypes>();
    const data = useData();
    const { progressionOverview: getProgressionOverviewUrl } =
        useDashboardRouter();

    const currentClassroom = useMemo<ClassroomAnalytics | undefined>(
        () => classrooms.find((classroom) => classroom.id === classroomId),
        [classroomId, classrooms]
    );
    const currentModule = useMemo((): ModuleDashboard | undefined => {
        return currentClassroom?.modulesList.find(
            (module) => module.id === moduleId
        );
    }, [currentClassroom, moduleId]);
    return (
        <div className="clustering__nav">
            <Select
                label={intl.formatMessage(messages.selectClassroom)}
                className="clustering__select"
                onSelectOption={(value) => {
                    history.push(
                        path
                            .replace(/:classroomId/, value)
                            .replace(/:moduleId/, moduleId)
                    );
                }}
                selectedOption={
                    currentClassroom
                        ? `${intl.formatMessage(messages.class)} ${
                              currentClassroom.name
                          }`
                        : ""
                }
            >
                {classrooms.map((classroom, i) => (
                    <option key={i} value={classroom.id}>
                        {`${intl.formatMessage(messages.class)} ${
                            classroom.name
                        }`}
                    </option>
                ))}
            </Select>

            {currentClassroom && currentClassroom.modulesList.length > 0 && (
                <Select
                    label={intl.formatMessage(messages.selectModule)}
                    className="clustering__select"
                    onSelectOption={(value) => {
                        history.push(
                            path
                                .replace(/:moduleId/, value)
                                .replace(/:classroomId/, classroomId)
                        );
                    }}
                    selectedOption={getModuleName(currentModule?.id, data)}
                >
                    {currentClassroom.modulesList.map((module, i) => (
                        <option key={i} value={module.id}>
                            {getModuleName(module.id, data)}
                        </option>
                    ))}
                </Select>
            )}

            <Button
                className="clustering__see-progression"
                label={intl.formatMessage(messages.seeProgression)}
                icons={[
                    {
                        path: "arrow_right",
                        placement: "right",
                    },
                ]}
                onClick={() =>
                    history.push(
                        getProgressionOverviewUrl(classroomId, moduleId)
                    )
                }
                variant="custom"
                colors={{
                    background: "var(--element-teacher-default)",
                    text: "var(--text-main-inverted)",
                    hoverBackground: "var(--element-button-custom-hover)",
                }}
            />
        </div>
    );
};

export default ClusteringNav;

const messages = defineMessages({
    seeProgression: {
        id: "clustering-seeProgressionOverview",
        defaultMessage: "See progression overview",
    },
    class: {
        id: "dashboard-common-class",
        defaultMessage: "Class",
    },
    selectClassroom: {
        id: "dashboard-common-select-classroom",
        defaultMessage: "Select a classroom",
    },
    selectModule: {
        id: "dashboard-common-select-module",
        defaultMessage: "Select a module",
    },
});
